import React from 'react';
import { Image } from '@adobe/react-spectrum';
import { getProductIcons } from '../../../utils/categoryIconUtils';
import MultiProductPlusIcon from '../../assets/icons/multi_product_plus.svg';
import styles from './TimelineCard.module.scss';

interface ProductIconCompactProps {
  products?: string[] | string;
}

/**
 * ProductIconCompact - Displays product icon(s) for small cards
 *
 * Behavior based on Figma design:
 * - Single product: Shows one 16x16 product icon
 * - Multiple products: Shows first product icon (16x16) with small + indicator overlay
 * - The container always occupies 16x16 to prevent layout shift
 *
 * Figma reference: Frame 26086624 (node-id=14474-102895)
 * - Product icon: B_app_Illustrator (16x16, border-radius: 5.5px)
 * - + icon overlay: Frame 26086776 (8x8, positioned absolute top-right)
 */
export const ProductIconCompact: React.FC<ProductIconCompactProps> = ({
  products
}) => {
  if (!products) return null;

  const productsArray = Array.isArray(products) ? products : [products];
  const productIcons = getProductIcons(products);

  // No product icons to show
  if (productIcons.length === 0) return null;

  const isMultiProduct = productsArray.length > 1;
  const firstProductIcon = productIcons[0];

  return (
    <div className={styles.productIconContainer}>
      <Image
        src={firstProductIcon}
        alt={productsArray[0]}
        UNSAFE_className={styles.productIcon}
      />
      {isMultiProduct && (
        <div className={styles.multiProductIndicator}>
          <Image
            src={MultiProductPlusIcon}
            alt="Multiple products"
            UNSAFE_className={styles.plusIcon}
          />
        </div>
      )}
    </div>
  );
};
